import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { CheckCircle2, Loader2, User, Bot, Terminal, FileCode, Search, Zap } from 'lucide-react';
import { Icon } from '@iconify/react';
import { useStudioStore } from '@/store/useStudioStore';
import { useChatStore } from '@/store/useChatStore';
import { useChatService } from '@/hooks/studio/useChatService';
import { useSSEGenerate } from '@/hooks/studio/useSSEGenerate';
import { getToolCallMessageContent, parseMaybeJsonContent } from '@/lib/agentChatUtils';
import { cn } from '@/lib/utils';
import { StudioBuildProgress } from './StudioBuildProgress';
import { StudioBuildFailPanel } from './StudioBuildFailPanel';
import { isSpecialProjectType } from '@/lib/projectUtils';
import { getStudioBuildCopy } from './studioBuildCopy';
import { trackEvent } from '@/api/track';
import {
  buildCloneGenerateRequestConfig,
  buildProductGenerateRequestConfig,
  INVALID_CLONE_SOURCE_URL_MESSAGE,
} from '@/lib/webpageGeneration';

interface DisplayStep {
  id: string;
  label: string;
  role: string;
  toolName?: string;
  type?: string;
  toolInput?: unknown;
}

/**
 * 根据消息属性获取对应的图标
 */
const getStepIcon = (m: DisplayStep) => {
  if (m.role === 'user') return <User size={10} />;
  if (m.toolName) {
    switch (m.toolName) {
      case 'Bash':
        return <Terminal size={10} />;
      case 'Write':
      case 'Read':
      case 'Edit':
        return <FileCode size={10} />;
      case 'Glob':
        return <Search size={10} />;
      default:
        return <Zap size={10} />;
    }
  }
  return <Bot size={10} />;
};

/**
 * 构建状态的主进度展示 (集成 Agent 实时对话展示)
 * 设计为在 StudioPreview 区域内展示
 */
export const StudioBuildStage = () => {
  const { t } = useTranslation('studio');
  const {
    setIsBuilding,
    setIsBuildDone,
    isBuildDone,
    currentProjectTypeId,
    pendingCommand,
    pendingGenerateRequestConfig,
    buildVariant,
    cloneSourceUrl,
    productRequestConfig,
    buildTrackingContext,
    setBuildTrackingContext,
    currentId,
    currentProjectId,
  } = useStudioStore();
  const { sendCommand } = useChatService();
  const { generateProject } = useSSEGenerate();
  const buildCopy = getStudioBuildCopy(buildVariant);

  const { messages, isBuildFailed, buildFailReason, setBuildError } = useChatStore();
  const scrollRef = useRef<HTMLDivElement>(null);
  const failureTrackedRef = useRef(false);

  // 完成后倒计时自动进入预览
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    if (!isBuildDone) return;

    let remaining = 5;

    const timer = setInterval(() => {
      remaining -= 1;
      setCountdown(remaining);
      if (remaining <= 0) {
        clearInterval(timer);
        setIsBuilding(false);
      }
    }, 1000);

    return () => {
      clearInterval(timer);
      setCountdown(5000000);
    };
  }, [isBuildDone, setIsBuilding]);

  useEffect(() => {
    if (buildVariant !== 'standard') return;

    const appId = currentProjectId ?? currentId;
    if (!appId) return;

    const dedupeKey = `app_creation_start_${appId}`;
    if (sessionStorage.getItem(dedupeKey)) return;
    sessionStorage.setItem(dedupeKey, '1');

    trackEvent({
      eventCode: 'app_creation_start',
      eventData: JSON.stringify({
        app_id: appId,
        create_method: buildTrackingContext?.createMethod || 'brief',
        build_variant: buildVariant,
        prompt_length: buildTrackingContext?.promptLength,
        section_count: buildTrackingContext?.sectionCount,
        has_website_url: buildTrackingContext?.hasWebsiteUrl,
        template_method: buildTrackingContext?.templateMethod ?? undefined,
      }),
    });
    setBuildTrackingContext(null);
  }, [buildTrackingContext, buildVariant, currentId, currentProjectId, setBuildTrackingContext]);

  useEffect(() => {
    if (!isBuildFailed) {
      failureTrackedRef.current = false;
      return;
    }
    if (failureTrackedRef.current) return;

    failureTrackedRef.current = true;
    trackEvent({
      eventCode: 'app_creation_failed',
      eventData: JSON.stringify({
        app_id: currentProjectId ?? currentId ?? undefined,
        build_variant: buildVariant,
        error: buildFailReason || 'Unknown build failure',
      }),
    });
  }, [buildFailReason, buildVariant, currentId, currentProjectId, isBuildFailed]);

  const handleRetry = () => {
    setBuildError(false, null);

    if (isSpecialProjectType(currentProjectTypeId)) {
      sendCommand(
        t('build.status.retry_command', 'The build failed, please retry the generation.'),
        {
          hidden: true,
        },
      );
    } else {
      const command =
        pendingCommand ||
        t('build.status.retry_command', 'The build failed, please retry the generation.');
      const cloneRequestConfig =
        buildVariant === 'clone' ? buildCloneGenerateRequestConfig(cloneSourceUrl) : null;
      const requestConfig =
        pendingGenerateRequestConfig ||
        (buildVariant === 'product'
          ? productRequestConfig || buildProductGenerateRequestConfig()
          : cloneRequestConfig);

      if (buildVariant === 'clone' && !cloneRequestConfig) {
        setBuildError(true, INVALID_CLONE_SOURCE_URL_MESSAGE);
        return;
      }

      generateProject(command, undefined, undefined, requestConfig ?? undefined);
    }
  };

  // 1. 将所有消息转换为可视化步骤
  const displaySteps = useMemo(() => {
    const steps = messages
      .map((m): DisplayStep | null => {
        let label = m.content
          ? typeof m.content === 'string'
            ? m.content
            : JSON.stringify(m.content)
          : t('build.status.building_generic');

        if (m.toolName) {
          label = getToolCallMessageContent(m.toolName, m.toolInput);
        }

        const { cleanText, parsed } = parseMaybeJsonContent(label);

        const messageType = parsed?.type as string | undefined;
        if (messageType && ['expert_review', 'creation_suggestions'].includes(messageType)) {
          return null;
        }

        return {
          id: m.id,
          label: cleanText || label,
          role: m.role,
          toolName: m.toolName,
          toolInput: m.toolInput,
        };
      })
      .filter((step): step is DisplayStep => step !== null);

    if (steps.length === 0) {
      return [
        { id: 'init', label: t('build.status.initializing'), role: 'system', type: 'system' },
      ];
    }
    return steps;
  }, [messages, t]);

  // 2. 自动滚动到底部：使用小延迟确保 DOM 已经绘制并计算好新高度，从而使平滑滚动生效
  useEffect(() => {
    if (scrollRef.current) {
      const el = scrollRef.current;
      const timer = setTimeout(() => {
        el.scrollTo({
          top: el.scrollHeight,
          behavior: 'smooth',
        });
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [displaySteps.length, isBuildDone]);

  return (
    <div className="flex min-h-full flex-1 flex-col items-center justify-center overflow-x-hidden overflow-y-auto bg-[#F7F7FC] p-4 sm:p-[24px_18px] lg:p-[30px_22px]">
      <style>{`
        @keyframes stepFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-step-in {
          animation: stepFadeIn 0.6s ease-out forwards;
        }
      `}</style>
      <div className="animate-in slide-in-from-bottom-4 flex w-full max-w-[820px] flex-col rounded-[14px] border border-[rgba(0,0,0,0.07)] bg-white p-4 shadow-[0_12px_38px_rgba(0,0,0,0.07)] duration-700 sm:rounded-[16px] sm:p-5 lg:p-6">
        {/* 顶部状态标签 */}
        <div className="mb-3 inline-flex items-center gap-[7px] self-start rounded-full border border-[rgba(37,99,235,0.2)] bg-[rgba(37,99,235,0.09)] p-[6px_11px] text-[11px] font-bold text-[#2563EB]">
          {isBuildFailed ? (
            <i className="fa fa-circle-exclamation text-[12px]" />
          ) : isBuildDone ? (
            <Icon icon="fa6-solid:circle-check" className="h-3 w-3" />
          ) : (
            <i className="fa fa-gear fa-spin text-[12px]" />
          )}
          {isBuildFailed
            ? t('build.status.failed_badge', 'Build failed')
            : isBuildDone
              ? t('build.status.completed')
              : buildCopy.badge}
        </div>

        <h1 className="mb-2 text-[24px] leading-[1.14] font-bold tracking-[-0.6px] sm:text-[28px] lg:text-[30px] lg:tracking-[-0.7px]">
          {isBuildFailed
            ? t('build.status.failed_title', 'This page build did not finish')
            : isBuildDone
              ? t('build.status.ready_title')
              : buildCopy.title}
        </h1>
        <h2 className="mb-4 text-[13px] leading-[1.7] font-normal text-[rgba(13,13,26,0.56)] sm:text-[14px]">
          {isBuildFailed
            ? t(
                'build.status.failed_sub',
                'The draft stopped before it could enter review. You can check the failure reason below and start a new build attempt.',
              )
            : buildCopy.subtitle}
        </h2>

        {isBuildFailed ? (
          <StudioBuildFailPanel reason={buildFailReason || undefined} onRetry={handleRetry} />
        ) : (
          <StudioBuildProgress onDone={setIsBuildDone} />
        )}

        {/* 动态步骤列表 */}
        {!isBuildFailed && (
          <div
            ref={scrollRef}
            className="flex max-h-[300px] flex-col gap-1 overflow-y-auto rounded-[12px] border border-[rgba(0,0,0,0.07)] bg-[#F7F7FC] p-2"
          >
            {displaySteps.map((step, index) => {
              const isLast = index === displaySteps.length - 1;
              const isActive = isLast && !isBuildDone;
              const isCompleted = !isLast || isBuildDone;

              return (
                <div
                  key={step.id}
                  className={cn(
                    'animate-step-in mb-2 flex items-center gap-[9px] rounded-[9px] p-[8px_9px] text-[12.5px] transition-colors duration-300',
                    isCompleted
                      ? 'bg-[rgba(5,150,105,0.07)] text-[#047857]'
                      : isActive
                        ? 'bg-[rgba(37,99,235,0.08)] font-bold text-[#2563EB]'
                        : 'text-[rgba(13,13,26,0.38)]',
                  )}
                >
                  <div
                    className={cn(
                      'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full text-[9px]',
                      isCompleted
                        ? 'bg-[rgba(5,150,105,0.15)] text-[#059669]'
                        : isActive
                          ? 'bg-[rgba(37,99,235,0.18)] text-[#2563EB]'
                          : 'bg-[rgba(0,0,0,0.08)] text-[rgba(0,0,0,0.4)]',
                    )}
                  >
                    {isCompleted && step.role !== 'user' ? (
                      <CheckCircle2 size={10} />
                    ) : (
                      getStepIcon(step)
                    )}
                  </div>
                  <span className="flex-1 break-words">{String(step.label)}</span>
                  {isActive && <Loader2 size={10} className="animate-spin opacity-40" />}
                </div>
              );
            })}
          </div>
        )}

        {/* 完成后的面板 */}
        {isBuildDone && (
          <div className="animate-in fade-in slide-in-from-top-2 mt-4 flex flex-col items-center gap-[14px] rounded-[14px] border border-[rgba(5,150,105,0.25)] bg-gradient-to-br from-[rgba(5,150,105,0.06)] to-[rgba(5,150,105,0.02)] p-[20px_16px_18px] duration-500">
            <button
              onClick={() => setIsBuilding(false)}
              className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-[11px] border-none bg-gradient-to-r from-[#6D28D9] to-[#0891B2] p-[13px_20px] font-bold text-white shadow-[0_6px_20px_rgba(109,40,217,0.28)] transition-all hover:scale-[1.02] hover:shadow-[0_10px_28px_rgba(109,40,217,0.38)] active:scale-[0.98] sm:w-auto sm:p-[13px_28px]"
            >
              {t('build.status.start_review')}
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <path
                  d="M3 8h10M9.5 4.5l3.5 3.5-3.5 3.5"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <div className="flex items-center gap-1.5 text-[13px] text-[rgba(13,13,26,0.56)]">
              <Trans
                i18nKey="build.status.auto_enter_hint"
                ns="studio"
                values={{ count: countdown }}
                components={{ bold: <strong className="font-bold text-[#0D0D1A]" /> }}
              />
            </div>
          </div>
        )}
      </div>

      {!isBuildFailed && !isBuildDone ? (
        <div
          className="mt-3 flex w-full max-w-[820px] items-start gap-[9px] rounded-[10px] border border-[rgba(8,145,178,0.18)] bg-[rgba(8,145,178,0.05)] px-[14px] py-[11px] text-[12.5px] leading-[1.55] font-medium text-[rgba(13,13,26,0.56)]"
          role="status"
          aria-live="polite"
        >
          <i className="fa fa-envelope mt-[2px] text-[12px] text-[#0891B2]" aria-hidden="true" />
          <span>When your page is ready, we'll email you.</span>
        </div>
      ) : null}
    </div>
  );
};
